import React, { useEffect, useState } from 'react';
import { parentDeviceAPI, logsAPI } from '../../api/client';
import { PageHeader } from '../../components/UI';
import T from '../../theme';

const LOG_ICONS = {
  zone_enter: '📍', zone_exit: '🚶',
  signal_lost: '📡', signal_restored: '✅', low_battery: '🔋',
  device_activated: '🔗', device_removed: '❌',
};

const EVENT_COLORS = {
  zone_enter: '#3B82F6', zone_exit: T.orange,
  signal_lost: T.coral, signal_restored: T.blue,
  low_battery: T.yellow,
  device_activated: T.blue, device_removed: T.coral,
};

export default function LogsPage() {
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [logs, setLogs] = useState([]);
  const [signalLosses, setSignalLosses] = useState([]);
  const [tab, setTab] = useState('logs');
  const [loading, setLoading] = useState(true);
  const [eventFilter, setEventFilter] = useState('');

  useEffect(() => {
    parentDeviceAPI.list().then(r => {
      setDevices(r.data);
      if (r.data.length > 0) setSelectedDevice(r.data[0]);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedDevice) return;
    const params = eventFilter ? `event_type=${eventFilter}&limit=100` : 'limit=100';
    Promise.all([
      logsAPI.activityLogs(selectedDevice.id, params),
      logsAPI.signalLosses(selectedDevice.id),
    ]).then(([logRes, slRes]) => {
      setLogs(logRes.data);
      setSignalLosses(slRes.data);
    }).catch(() => {});
  }, [selectedDevice, eventFilter]);

  // Group logs by date
  const groupedLogs = logs.reduce((acc, log) => {
    const date = new Date(log.created_at).toLocaleDateString('uz-UZ');
    if (!acc[date]) acc[date] = [];
    acc[date].push(log);
    return acc;
  }, {});

  return (
    <div>
      <PageHeader title="📋 Jurnal" />
      {/* Device selector + filter */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        {devices.length > 1 && devices.map(d => (
          <button key={d.id} onClick={() => setSelectedDevice(d)} style={{
            padding: '8px 16px', background: selectedDevice?.id === d.id ? `linear-gradient(135deg, ${T.blue}, ${T.blueLight})` : '#fff',
            border: `2px solid ${selectedDevice?.id === d.id ? T.blue : T.border}`, borderRadius: 12,
            cursor: 'pointer', fontFamily: T.font, fontSize: 13, fontWeight: 700,
            color: selectedDevice?.id === d.id ? '#fff' : T.textSec, boxShadow: T.shadow,
          }}>📡 {d.child_info?.name || d.imei}</button>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 20, background: '#fff', borderRadius: 14, padding: 5, boxShadow: T.shadow, width: 'fit-content' }}>
        {[
          { key: 'logs', label: 'Hodisalar', count: logs.length },
          { key: 'signal', label: 'Signal', count: signalLosses.length },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: '10px 18px', background: tab === t.key ? `linear-gradient(135deg, ${T.blue}, ${T.blueLight})` : 'transparent',
            border: 'none', borderRadius: 10, cursor: 'pointer', fontFamily: T.font,
            fontSize: 14, fontWeight: 800, color: tab === t.key ? '#fff' : T.textMuted,
          }}>{t.label} ({t.count})</button>
        ))}
      </div>

      {tab === 'logs' && (
        <>
          {/* Event type filter */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 20, flexWrap: 'wrap' }}>
            <button onClick={() => setEventFilter('')} style={{
              padding: '6px 14px', borderRadius: 20, border: `1.5px solid ${!eventFilter ? T.blue : T.border}`,
              background: !eventFilter ? T.blueSoft : '#fff', fontSize: 12, fontWeight: 700,
              color: !eventFilter ? T.blueDark : T.textMuted, cursor: 'pointer', fontFamily: T.font,
            }}>Hammasi</button>
            {Object.entries(LOG_ICONS).map(([key, icon]) => (
              <button key={key} onClick={() => setEventFilter(key)} style={{
                padding: '6px 14px', borderRadius: 20, border: `1.5px solid ${eventFilter === key ? EVENT_COLORS[key] || T.blue : T.border}`,
                background: eventFilter === key ? (EVENT_COLORS[key] || T.blue) + '15' : '#fff',
                fontSize: 12, fontWeight: 700, color: eventFilter === key ? EVENT_COLORS[key] || T.text : T.textMuted,
                cursor: 'pointer', fontFamily: T.font,
              }}>{icon}</button>
            ))}
          </div>

          {/* Logs by date */}
          {Object.keys(groupedLogs).length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', background: '#fff', borderRadius: 16, boxShadow: T.shadow }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>📋</div>
              <h3 style={{ fontSize: 18, fontWeight: 900, color: T.text, marginBottom: 6 }}>Hali qaydlar yo'q</h3>
              <p style={{ fontSize: 14, color: T.textMuted }}>Hodisalar avtomatik qayd qilinadi</p>
            </div>
          ) : (
            Object.entries(groupedLogs).map(([date, dateLogs]) => (
              <div key={date} style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: T.textMuted, marginBottom: 10, letterSpacing: 0.5 }}>{date}</div>
                <div style={{ background: '#fff', borderRadius: 14, boxShadow: T.shadow, overflow: 'hidden' }}>
                  {dateLogs.map((log, i) => (
                    <div key={log.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', borderBottom: i < dateLogs.length - 1 ? `1px solid ${T.border}` : 'none' }}>
                      <span style={{ fontSize: 20, width: 28, textAlign: 'center' }}>{LOG_ICONS[log.event_type] || '📌'}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{log.message}</div>
                        {log.child_name && <span style={{ fontSize: 12, color: T.textMuted }}>{log.child_name}</span>}
                      </div>
                      <span style={{ fontSize: 12, fontWeight: 700, color: T.textMuted, fontFamily: T.mono, whiteSpace: 'nowrap' }}>
                        {new Date(log.created_at).toLocaleTimeString('uz-UZ', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </>
      )}

      {tab === 'signal' && (
        signalLosses.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', background: '#fff', borderRadius: 16, boxShadow: T.shadow }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>📡</div>
            <h3 style={{ fontSize: 18, fontWeight: 900, color: T.text, marginBottom: 6 }}>Signal yo'qotishlar yo'q</h3>
            <p style={{ fontSize: 14, color: T.textMuted }}>Ajoyib — barcha qurilmalar aloqada!</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {signalLosses.map(sl => (
              <div key={sl.id} style={{ background: '#fff', borderRadius: 14, padding: '18px', boxShadow: T.shadow, border: sl.is_ongoing ? `2px solid ${T.coral}30` : `1px solid ${T.border}` }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <span style={{ fontSize: 14, fontWeight: 800, color: sl.is_ongoing ? T.coral : T.text }}>
                    📡 {sl.is_ongoing ? 'Hozir signal yo\'q!' : 'Signal yo\'qolgan edi'}
                  </span>
                  <span style={{ padding: '4px 12px', borderRadius: 10, fontSize: 12, fontWeight: 800, background: sl.is_ongoing ? '#FEF2F2' : T.blueSoft, color: sl.is_ongoing ? T.coral : T.blue }}>
                    {sl.is_ongoing ? '🔴 Davom etmoqda' : `✅ ${sl.duration_minutes} daqiqa`}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 16, fontSize: 13, color: T.textMuted }}>
                  <span>🕐 Boshlanish: {new Date(sl.started_at).toLocaleString('uz-UZ')}</span>
                  {sl.ended_at && <span>🕐 Tugash: {new Date(sl.ended_at).toLocaleString('uz-UZ')}</span>}
                </div>
                {sl.last_address && <div style={{ marginTop: 8, fontSize: 13, color: T.textSec }}>📍 {sl.last_address}</div>}
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}
